import React from 'react';

const QuantLibExamples = () => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(550px, 1fr))',
      gap: '40px',
      marginTop: '30px'
    }}>
      {/* Approach 1: Single Agent Example */}
      <div style={{
        background: 'rgba(255, 107, 107, 0.1)',
        padding: '30px',
        borderRadius: '16px',
        border: '1px solid rgba(255, 107, 107, 0.3)'
      }}>
        <h4 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px',
          color: '#FF6B6B',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🤖 Single Agent Approach
        </h4>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '20px',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '15px', color: '#FF6B6B' }}>
            <strong>User:</strong> "Optimize the performance of my code"
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
            <em>Context: QuantLib Monte Carlo simulations</em>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#FF9999' }}>Agent's Single Solution:</strong>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '15px',
            borderRadius: '8px',
            fontSize: '13px',
            lineHeight: '1.8'
          }}>
            <div style={{ marginBottom: '10px' }}>
              • Added basic parallelization with OpenMP
            </div>
            <div style={{ marginBottom: '10px' }}>
              • Cached some repeated calculations
            </div>
            <div style={{ marginBottom: '10px' }}>
              • Minor memory allocation improvements
            </div>
            <div style={{ 
              marginTop: '15px',
              paddingTop: '15px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <strong>Result:</strong> <span style={{ color: '#FF6B6B' }}>~5% performance gain</span>
            </div>
          </div>
          
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'rgba(255, 107, 107, 0.1)',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <strong>❌ Missed Opportunities:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
              <li>Didn't identify SIMD vectorization potential</li>
              <li>Overlooked custom memory pools</li>
              <li>No algorithmic improvements suggested</li>
              <li>Single perspective limited optimization scope</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Approach 2: Current Artemis Example */}
      <div style={{
        background: 'rgba(78, 205, 196, 0.1)',
        padding: '30px',
        borderRadius: '16px',
        border: '1px solid rgba(78, 205, 196, 0.3)'
      }}>
        <h4 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px',
          color: '#4ECDC4',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🚀 Current Artemis Approach
        </h4>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '20px',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '15px', color: '#4ECDC4' }}>
            <strong>User:</strong> "Optimize the performance of my code"
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
            <em>Context: QuantLib Monte Carlo simulations</em>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#96CEB4' }}>Artemis Evolution Process:</strong>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              {
                branch: 'evolution-1',
                approach: 'Memory optimization focus',
                changes: ['Custom memory pools', 'Stack allocation for small objects', 'Reduced allocations'],
                gain: '+12%',
                color: '#FF6B6B'
              },
              {
                branch: 'evolution-2',
                approach: 'SIMD vectorization',
                changes: ['AVX2 intrinsics for RNG', 'Vectorized path calculations', 'Batch processing'],
                gain: '+18%',
                color: '#96CEB4'
              },
              {
                branch: 'evolution-3',
                approach: 'Algorithm improvements',
                changes: ['Sobol sequences instead of MT', 'Variance reduction techniques', 'Smart path recycling'],
                gain: '+22%',
                color: '#DDA0DD'
              },
              {
                branch: 'evolution-4',
                approach: 'Hybrid best solution',
                changes: ['Combined SIMD + Sobol', 'Memory pools + vectorization', 'GPU offloading for large batches'],
                gain: '+30%',
                color: '#4ECDC4',
                best: true
              }
            ].map((evolution, i) => (
              <div key={i} style={{
                background: evolution.best 
                  ? `linear-gradient(135deg, ${evolution.color}20, ${evolution.color}10)`
                  : 'rgba(255, 255, 255, 0.05)',
                padding: '15px',
                borderRadius: '8px',
                border: evolution.best 
                  ? `2px solid ${evolution.color}60`
                  : '1px solid rgba(255, 255, 255, 0.1)',
                fontSize: '13px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <span style={{ 
                    color: evolution.color,
                    fontWeight: '600'
                  }}>
                    {evolution.branch}: {evolution.approach}
                  </span>
                  <span style={{
                    background: evolution.best ? evolution.color : 'rgba(255, 255, 255, 0.1)',
                    color: evolution.best ? 'white' : evolution.color,
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {evolution.gain}
                  </span>
                </div>
                <ul style={{ margin: '0', paddingLeft: '20px', opacity: 0.9 }}>
                  {evolution.changes.map((change, idx) => (
                    <li key={idx} style={{ marginBottom: '3px' }}>{change}</li>
                  ))}
                </ul>
                {evolution.best && (
                  <div style={{
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(78, 205, 196, 0.3)',
                    color: '#4ECDC4',
                    fontWeight: '600',
                    fontSize: '12px'
                  }}>
                    ✅ SELECTED - Best combination of techniques
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'rgba(78, 205, 196, 0.1)',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <strong>✅ Artemis Advantages:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
              <li>Explored multiple optimization strategies</li>
              <li>Combined best techniques from different approaches</li>
              <li>Validated performance gains through testing</li>
              <li>Achieved <strong style={{ color: '#4ECDC4' }}>30% performance improvement</strong></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Approach 3: Multi-Agent Example */}
      <div style={{
        background: 'rgba(244, 81, 30, 0.1)',
        padding: '30px',
        borderRadius: '16px',
        border: '2px solid rgba(244, 81, 30, 0.3)',
        gridColumn: 'span 2'
      }}>
        <h4 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '20px',
          color: '#F4511E',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          🔥 Multi-Agent Artemis Approach
          <span style={{
            background: 'rgba(244, 81, 30, 0.2)',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            Projected
          </span>
        </h4>
        
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          padding: '20px',
          borderRadius: '10px',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '15px', color: '#F4511E' }}>
            <strong>User:</strong> "Optimize the performance of my code"
          </div>
          <div style={{ marginBottom: '10px', fontSize: '12px', opacity: 0.8 }}>
            <em>Context: QuantLib Monte Carlo simulations</em>
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#FF6D00' }}>Multi-Agent Competition & Evolution:</strong>
          </div>
          
          {/* Round 1: Initial approaches */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '20px',
            borderRadius: '10px',
            marginBottom: '20px'
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#F4511E' }}>
              Round 1: Independent Agent Solutions
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '12px'
            }}>
              {[
                {
                  agent: 'GPT-4',
                  icon: '🤖',
                  approach: 'Functional approach with immutability',
                  techniques: ['Persistent data structures', 'Lazy evaluation', 'Memoization'],
                  gain: '+15%',
                  color: '#FF6B6B'
                },
                {
                  agent: 'Claude',
                  icon: '🧠',
                  approach: 'Architecture redesign for parallelism',
                  techniques: ['Task decomposition', 'Lock-free algorithms', 'NUMA awareness'],
                  gain: '+25%',
                  color: '#4ECDC4'
                },
                {
                  agent: 'Gemini',
                  icon: '⚡',
                  approach: 'Low-level optimization',
                  techniques: ['AVX-512 intrinsics', 'Cache-line optimization', 'Branch prediction'],
                  gain: '+28%',
                  color: '#96CEB4'
                },
                {
                  agent: 'Llama',
                  icon: '🦙',
                  approach: 'Algorithmic improvements',
                  techniques: ['Quasi-Monte Carlo', 'Importance sampling', 'Control variates'],
                  gain: '+35%',
                  color: '#DDA0DD'
                }
              ].map((agent, i) => (
                <div key={i} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  padding: '15px',
                  background: `${agent.color}15`,
                  borderRadius: '8px',
                  border: `1px solid ${agent.color}30`,
                  fontSize: '13px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{agent.icon}</span>
                      <span style={{ 
                        color: agent.color, 
                        fontWeight: '600'
                      }}>
                        {agent.agent}
                      </span>
                    </div>
                    <span style={{
                      background: agent.color,
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {agent.gain}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: '500' }}>
                    {agent.approach}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    opacity: 0.8,
                    display: 'flex',
                    gap: '6px',
                    flexWrap: 'wrap'
                  }}>
                    {agent.techniques.map((tech, idx) => (
                      <span key={idx} style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        padding: '3px 8px',
                        borderRadius: '4px'
                      }}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Round 2: Cross-pollination */}
          <div style={{
            background: 'rgba(244, 81, 30, 0.1)',
            padding: '20px',
            borderRadius: '10px',
            marginBottom: '20px',
            border: '1px solid rgba(244, 81, 30, 0.3)'
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#F4511E' }}>
              Round 2: Artemis-Guided Cross-Pollination
            </div>
            
            <div style={{ fontSize: '13px', lineHeight: '1.8', marginBottom: '15px' }}>
              <strong>Artemis Feedback:</strong>
              <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
                <li>Combine Llama's algorithms with Gemini's low-level optimizations</li>
                <li>Apply Claude's parallelism to GPT-4's functional structures</li>
                <li>Integrate SIMD across all mathematical operations</li>
              </ul>
            </div>
            
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '13px'
            }}>
              <strong>New Hybrid Approaches:</strong>
              <div style={{ marginTop: '10px', lineHeight: '1.8' }}>
                • GPT-4 + Claude: Functional parallelism → <span style={{ color: '#4ECDC4', fontWeight: '600' }}>+40%</span><br/>
                • Gemini + Llama: SIMD Quasi-MC → <span style={{ color: '#96CEB4', fontWeight: '600' }}>+45%</span>
              </div>
            </div>
          </div>

          {/* Round 3: Final evolution */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(244, 81, 30, 0.2), rgba(255, 109, 0, 0.1))',
            padding: '20px',
            borderRadius: '10px',
            border: '2px solid rgba(244, 81, 30, 0.5)'
          }}>
            <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '15px', color: '#F4511E' }}>
              Round 3: Final Evolution - Best of All Worlds
            </div>
            
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '15px',
              borderRadius: '8px',
              fontSize: '13px',
              lineHeight: '1.8'
            }}>
              <strong style={{ color: '#FF6D00' }}>Ultimate Solution:</strong>
              <ul style={{ margin: '10px 0 0 20px', paddingLeft: '0' }}>
                <li>Quasi-Monte Carlo with Sobol sequences (Llama)</li>
                <li>AVX-512 vectorized path generation (Gemini)</li>
                <li>Lock-free parallel task distribution (Claude)</li>
                <li>Functional caching & memoization (GPT-4)</li>
                <li>GPU offloading for large portfolios (Emergent)</li>
                <li>Custom memory allocators with NUMA pinning (Combined)</li>
              </ul>
              
              <div style={{
                marginTop: '15px',
                paddingTop: '15px',
                borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>
                  <strong>Projected Result:</strong>
                </span>
                <span style={{
                  background: 'linear-gradient(135deg, #F4511E, #FF6D00)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '20px',
                  fontWeight: '700',
                  fontSize: '16px'
                }}>
                  +50-60% performance gain
                </span>
              </div>
            </div>
          </div>
          
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: 'rgba(244, 81, 30, 0.1)',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <strong>🚀 Multi-Agent Advantages:</strong>
            <ul style={{ margin: '8px 0 0 20px', paddingLeft: '0' }}>
              <li>Each agent brings unique optimization perspective</li>
              <li>Iterative refinement discovers emergent solutions</li>
              <li>Combines techniques no single agent would suggest</li>
              <li>Projected <strong style={{ color: '#F4511E' }}>50-60% improvement</strong> vs 30% from single approach</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuantLibExamples; 